import * as React from 'react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import UserProfile from '../..';
import UserPayroll from '../index';
import { useEosContext } from '../hooks/useEosContext';
import { DatePicker as CustomDatePicker, formatDateForInput } from '../../../../components/dateTimePickers';
import { EligibilityGate } from './components/EligibilityGate';
import { LawVersionBanner } from './components/LawVersionBanner';
import { EosBreakdownTable } from './components/EosBreakdownTable';
import { EosComplianceChecklist } from './components/EosComplianceChecklist';
import ScenarioComparison from './components/ScenarioComparison';
import {
  PreviewEndOfService,
  ConfirmEndOfService,
  GetEndOfServiceTypes,
  GetEosAuditLog,
} from '../../../../api/Payroll/payroll';
import {
  DepartureReasonEnum,
  EosBreakdownView,
  EosAuditLogEntry,
  EndOfServiceTypeView,
  PreviewEosInput,
  isForCauseArt44,
  isForfeitableKsaArt80,
} from '../../../../models/payroll';
// CO-670 — the reason list and its label keys now live in one place, because the Exit Reasons tab in
// Lifecycle Settings maps the company's own exit reasons onto exactly these categories.
import { ALL_DEPARTURE_REASONS, departureReasonLabelKey } from '../../../../models/departureReasons';
import Select from '../../../../components/ui/Select';
import StickyActionBar from '../../../../components/ui/StickyActionBar';
import AppIcon from '../../../../components/AppIcon';
import { useToast, ToastRenderer } from '../../../../hooks/useToast';
import { useComplianceCountry } from '../../../../hooks/useComplianceCountry';
import { formatNumber } from '../../../../utils';
import { parseApiUtc } from '@/utils/apiDate';
import { currencyDisplay } from '@/utils/currencyDisplay';

type Props = Readonly<{
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  component?: React.ComponentType<any> | undefined;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  role?: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hireDate?: any;
  loggedInUserId?: string;
}>;

const CONTRACT_TYPES = [
  { value: 'Limited', labelKey: 'Limited' },
  { value: 'Unlimited', labelKey: 'Unlimited' },
];

const EOService = (props: Props) => {
  const { t } = useTranslation('common');
  const { id } = useParams();
  const userId = id ?? '';
  // EOS is HR-owned; Admin is a company admin, not HR (see CLAUDE.md Role Model). HR-only drives the calc.
  const isHr = props.role === 'Hr';
  // Employee self-view: viewing your own record without mutation rights → read-only banner + no forms/audit.
  const isOwnProfile = !!props.loggedInUserId && userId === props.loggedInUserId;
  const isSelfView = isOwnProfile && !isHr;

  const complianceCountry = useComplianceCountry();
  const isKsa = complianceCountry === 'KSA';
  const { context, loading: contextLoading, refetch } = useEosContext(userId);

  const [departureReason, setDepartureReason] = useState<DepartureReasonEnum | ''>('');
  const [contractType, setContractType] = useState<string>('');
  const [departureDate, setDepartureDate] = useState<string>('');
  const [endOfServiceId, setEndOfServiceId] = useState<number | null>(null);
  const [eosTypes, setEosTypes] = useState<EndOfServiceTypeView[]>([]);

  const [preview, setPreview] = useState<EosBreakdownView | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [allowGratuityDespiteCause, setAllowGratuityDespiteCause] = useState(false);
  // KSA Art. 80 procedural safeguards — forfeiture applies ONLY when both are ticked (opt-in).
  const [art80ProcedureConfirmed, setArt80ProcedureConfirmed] = useState(false);
  const [art80WarningOnFile, setArt80WarningOnFile] = useState(false);

  const [auditLog, setAuditLog] = useState<EosAuditLogEntry[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const { toast, showToast, dismissToast } = useToast();

  // CO-805: the currency label follows THE PROFILE OWNER, not the viewer. It now arrives on the EOS
  // context itself (`EosComplianceService.GetEosContext` -> `EmployeeCurrencyResolver`), because this is
  // an HR-only screen and the reader is almost never the employee. The previous source,
  // `state.user.CompanyCurrencyRate`, was a Redux key NO view model has ever carried — null for every
  // tenant, so every gratuity figure here rendered with no unit at all. The tempting client-side fix
  // (`state.user.userRules.currency`) is the signed-in HR user's currency and would label another
  // employee's gratuity in a plausible, wrong, unverifiable currency on a multi-currency tenant.
  // CO-804: `currencyDisplay` is code-first across both fields and uses explicit emptiness checks —
  // see utils/currencyDisplay.ts for why a `??`/`||` chain is not safe here.
  const currencyLabel = currencyDisplay(context);

  // Load EOS types once
  useEffect(() => {
    GetEndOfServiceTypes().then(r => {
      if (!r.errors?.length) setEosTypes(r.payload || []);
    }).catch(() => {});
  }, []);

  // Load audit log
  useEffect(() => {
    if (!userId) return;
    setAuditLoading(true);
    GetEosAuditLog(userId)
      .then(r => {
        if (!r.errors?.length) setAuditLog(r.payload || []);
      })
      .catch(() => {})
      .finally(() => setAuditLoading(false));
  }, [userId]);

  const runPreview = async (overrides?: {
    allowOverride?: boolean;
    procedureConfirmed?: boolean;
    warningOnFile?: boolean;
  }) => {
    if (!departureReason || !contractType || !departureDate || !endOfServiceId) {
      showToast(t('Please fill all required fields before previewing'), 'info');
      return;
    }
    setPreviewing(true);
    try {
      const input: PreviewEosInput = {
        userId,
        endOfServiceId,
        departureReason: departureReason as DepartureReasonEnum,
        contractType,
        departureDate,
        allowGratuityDespiteCause: overrides?.allowOverride ?? allowGratuityDespiteCause,
        art80ProcedureConfirmed: overrides?.procedureConfirmed ?? art80ProcedureConfirmed,
        art80WarningOnFile: overrides?.warningOnFile ?? art80WarningOnFile,
      };
      const result = await PreviewEndOfService(input);
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0]?.description ?? t('Preview failed'), 'error');
        return;
      }
      setPreview(result.payload);
    } catch {
      showToast(t('An unexpected error occurred'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handlePreview = () => runPreview();

  // Toggling any Art. 80 tick / the pay-anyway override re-previews so HR sees the forfeit/pay delta immediately.
  const handleToggleOverride = (val: boolean) => {
    setAllowGratuityDespiteCause(val);
    if (preview) void runPreview({ allowOverride: val });
  };
  const handleToggleProcedure = (val: boolean) => {
    setArt80ProcedureConfirmed(val);
    if (preview) void runPreview({ procedureConfirmed: val });
  };
  const handleToggleWarning = (val: boolean) => {
    setArt80WarningOnFile(val);
    if (preview) void runPreview({ warningOnFile: val });
  };

  const handleConfirm = async () => {
    if (!preview || !departureReason || !contractType || !departureDate || !endOfServiceId) return;
    setConfirming(true);
    try {
      const input: PreviewEosInput = {
        userId,
        endOfServiceId,
        departureReason: departureReason as DepartureReasonEnum,
        contractType,
        departureDate,
        allowGratuityDespiteCause,
        art80ProcedureConfirmed,
        art80WarningOnFile,
      };
      const result = await ConfirmEndOfService(input);
      if (result.errors && result.errors.length > 0) {
        showToast(result.errors[0]?.description ?? t('Failed to confirm EOS'), 'error');
        return;
      }
      showToast(t('EOS confirmed successfully'), 'success');
      setPreview(null);
      refetch();
    } catch {
      showToast(t('An unexpected error occurred'), 'error');
    } finally {
      setConfirming(false);
    }
  };

  const handleReset = () => {
    setPreview(null);
  };

  const isLoading = contextLoading || previewing || confirming;

  const eosTypeOptions = eosTypes.map(x => ({ label: String(x.type), value: x.id }));

  const departureReasonOptions = ALL_DEPARTURE_REASONS.map(r => ({
    label: t(r.labelKey),
    value: r.value,
  }));

  const contractTypeOptions = CONTRACT_TYPES.map(c => ({
    label: t(c.labelKey),
    value: c.value,
  }));

  if (complianceCountry !== 'UAE' && complianceCountry !== 'KSA') {
    return (
      <div className="flex flex-col flex-1">
        <UserProfile>
          <UserPayroll>
            <div className="relative flex flex-col gap-6 pt-5 pe-6">
              <div className="flex items-center gap-3">
                <AppIcon name="Hourglass" size={40} className="text-muted-foreground" />
                <h3 className="text-lg font-semibold text-muted-foreground">{t('END OF SERVICE')}</h3>
              </div>
              <div className="flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card py-12 px-6 text-center">
                <AppIcon name="Globe" size={48} className="text-muted-foreground" />
                <div>
                  <h3 className="text-lg font-semibold text-foreground">{t('End of Service is configured for UAE only')}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {t('End of Service calculation is currently scoped to UAE labour law. Support for your country will arrive in a future release.')}
                  </p>
                </div>
              </div>
            </div>
          </UserPayroll>
        </UserProfile>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1">
      <UserProfile>
        <UserPayroll>
          <div className="relative flex flex-col gap-6 pt-5 pe-6">
            {/* Header */}
            <div className="flex items-center gap-3">
              <AppIcon name="Hourglass" size={40} className="text-primary" />
              <h3 className="text-lg font-semibold text-primary">
                {isKsa ? t('END OF SERVICE (KSA LABOUR LAW)') : t('END OF SERVICE (UAE LABOUR LAW)')}
              </h3>
            </div>

            {/* Loading spinner while context loads */}
            {contextLoading && !context && (
              <div className="flex items-center justify-center py-16">
                <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            {context && (
              <>
                {/* Employee self-view — read-only notice */}
                {isSelfView && (
                  <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3">
                    <AppIcon name="Eye" size={18} className="mt-0.5 shrink-0 text-muted-foreground" />
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium text-foreground">{t('View only')}</p>
                      <p className="text-xs text-muted-foreground">
                        {t('This is your own end-of-service record. You can review the calculated entitlement, but only HR can make changes.')}
                      </p>
                    </div>
                  </div>
                )}

                {/* Eligibility gate */}
                {!context.isEligible ? (
                  <EligibilityGate
                    serviceYears={context.serviceYearsDecimal}
                    eligibleFrom={context.eligibleFrom}
                    country={complianceCountry}
                  />
                ) : (
                  <>
                    {/* Law version banner */}
                    <LawVersionBanner
                      lawVersion={context.lawVersion}
                      hireDate={context.hireDate}
                      country={complianceCountry}
                    />

                    {/* Salary info */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-card border border-border rounded-xl p-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Basic Salary')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatNumber(context.basicSalary)} {currencyLabel}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Gross Salary')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {formatNumber(context.grossSalary)} {currencyLabel}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Service Years')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {context.serviceYearsDecimal.toFixed(2)} {t('years')}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-0.5">{t('Leave Balance')}</p>
                        <p className="text-sm font-semibold text-foreground">
                          {context.leaveBalance} {t('days')}
                        </p>
                      </div>
                    </div>

                    {/* B2 — basic-salary gross-fallback warning */}
                    {context.noBasicConfigured && (
                      <div className="flex items-start gap-3 rounded-xl border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning">
                        <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                        <div className="flex flex-col gap-0.5">
                          <p className="font-semibold">{t('No basic salary configured')}</p>
                          <p className="text-warning/90">
                            {t('Gratuity is being calculated on gross pay because no salary component is flagged as the basic salary, which overstates the award. Flag the basic salary component in Salary Settings for an accurate gratuity.')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* CO-358 — B2's opposite twin. `noBasicConfigured` warns that the base was
                        SUBSTITUTED with gross; this warns there is NO base at all, so the award is 0.00.
                        The two are mutually exclusive server-side (one lives on the Auto branch, the other
                        on the pinned branch), so they never stack. Rendered destructive rather than warning
                        because the employee is being paid nothing, not merely an imprecise amount. */}
                    {context.isSilentZeroBase && (
                      <div className="flex items-start gap-3 rounded-xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                        <AppIcon name="AlertTriangle" size={16} className="mt-0.5 shrink-0" />
                        <div className="flex flex-col gap-0.5">
                          <p className="font-semibold">{t('Gratuity base is zero')}</p>
                          <p className="text-destructive/90">
                            {t('The End of Service base is set to basic salary only, but no salary component is flagged as the basic salary, so the base is 0.00 and this gratuity computes to zero. Flag the basic salary component in Salary Settings, or change the End of Service base mode.')}
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Existing EOS record info */}
                    {context.existingEos && (
                      <div className="flex items-start gap-3 rounded-xl border border-border bg-muted/40 px-4 py-3 text-sm">
                        <AppIcon name="Info" size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                        <div>
                          <p className="font-medium text-foreground">{t('Existing EOS Record')}</p>
                          <p className="text-muted-foreground">
                            {t('Amount')}: <span className="font-semibold">{formatNumber(Number(context.existingEos.amount))} {currencyLabel}</span>
                            {' — '}
                            {t('Departure')}: <span className="font-semibold">{t(departureReasonLabelKey(context.existingEos.departureReason || ''))}</span>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Form */}
                    {isHr && (
                      <div className="bg-card border border-border rounded-xl p-6 flex flex-col gap-5">
                        <h4 className="text-sm font-semibold text-foreground">{t('Calculate End of Service')}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                          {/* Departure Reason */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              {t('Departure Reason')} <span className="text-destructive">*</span>
                            </label>
                            <select
                              value={departureReason}
                              onChange={e => {
                                const val = e.target.value as DepartureReasonEnum;
                                setDepartureReason(val);
                                // Reset the pay-anyway override + Art. 80 ticks whenever the reason leaves a
                                // for-cause / forfeitable ground, so stale confirmations can't carry over.
                                if (!val || !isForCauseArt44(val)) setAllowGratuityDespiteCause(false);
                                if (!val || !isForfeitableKsaArt80(val)) {
                                  setArt80ProcedureConfirmed(false);
                                  setArt80WarningOnFile(false);
                                }
                              }}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            >
                              <option value="">{t('Select departure reason')}</option>
                              {departureReasonOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Contract Type */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              {t('Contract Type')} <span className="text-destructive">*</span>
                            </label>
                            <select
                              value={contractType}
                              onChange={e => setContractType(e.target.value)}
                              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                            >
                              <option value="">{t('Select contract type')}</option>
                              {contractTypeOptions.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                          </div>

                          {/* Departure Date */}
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1">
                              {t('Departure Date')} <span className="text-destructive">*</span>
                            </label>
                            <CustomDatePicker
                              label=""
                              value={departureDate || null}
                              onChange={(d: any) => setDepartureDate(d ? formatDateForInput(d) : '')}
                            />
                          </div>

                          {/* EOS Type */}
                          <div>
                            <Select
                              label={t('End Of Service Type')}
                              options={eosTypeOptions}
                              value={endOfServiceId}
                              onChange={(value: number | null) => setEndOfServiceId(value || null)}
                              searchable
                            />
                          </div>
                        </div>

                        {/* UAE for-cause (Art. 44) is INFORMATIONAL ONLY — FDL 33/2021 does not forfeit gratuity for
                            >= 1yr service, so this is a read-only non-forfeiture note (no override needed). */}
                        {!isKsa && departureReason && isForCauseArt44(departureReason) && (
                          <div className="flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-4 text-sm">
                            <AppIcon name="Info" size={16} className="text-muted-foreground mt-0.5 shrink-0" />
                            <div className="flex flex-col gap-0.5">
                              <p className="font-medium text-foreground">{t('For-cause termination recorded (Art. 44)')}</p>
                              <p className="text-xs text-muted-foreground">
                                {t('Under UAE FDL 33/2021 a for-cause reason does NOT forfeit end-of-service gratuity for employees with at least one year of service. The full calculated gratuity is paid.')}
                              </p>
                            </div>
                          </div>
                        )}

                        {/* KSA Art. 80 for-cause gratuity forfeiture — pay-by-default; forfeits ONLY when BOTH
                            procedural safeguards are confirmed. Keeps a pay-anyway override (Art. 80 is a right). */}
                        {isKsa && departureReason && isForfeitableKsaArt80(departureReason) && (
                          <div className="flex flex-col gap-3 rounded-lg border border-warning/40 bg-warning/10 p-4">
                            <div className="flex items-start gap-2">
                              <AppIcon name="AlertTriangle" size={16} className="text-warning mt-0.5 shrink-0" />
                              <div className="flex flex-col gap-0.5">
                                <p className="text-sm font-semibold text-foreground">{t('Art. 80 for-cause gratuity forfeiture')}</p>
                                <p className="text-xs text-muted-foreground">
                                  {t('KSA Labor Law Art. 80 permits forfeiting the full end-of-service gratuity for this ground, but only when the procedural safeguards below are met. Gratuity is paid in full unless BOTH are confirmed.')}
                                </p>
                              </div>
                            </div>

                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={art80ProcedureConfirmed}
                                onChange={e => handleToggleProcedure(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
                              />
                              <span className="text-sm font-medium text-foreground">
                                {t('The worker was given a chance to respond to the allegation')}
                              </span>
                            </label>

                            <label className="flex items-start gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={art80WarningOnFile}
                                onChange={e => handleToggleWarning(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
                              />
                              <span className="text-sm font-medium text-foreground">
                                {t('A written warning is on file')}
                              </span>
                            </label>

                            <p className="text-xs text-muted-foreground ps-7">
                              {t('For unexcused absence, Art. 80 forfeiture requires over 15 consecutive or over 30 intermittent days absent in the year (with a prior written warning). Confirm the KSA thresholds are met before forfeiting.')}
                            </p>

                            <label className="flex items-start gap-3 cursor-pointer border-t border-warning/30 pt-3">
                              <input
                                type="checkbox"
                                checked={allowGratuityDespiteCause}
                                onChange={e => handleToggleOverride(e.target.checked)}
                                className="mt-0.5 h-4 w-4 rounded border-input text-primary focus:ring-primary/20"
                              />
                              <span className="text-sm">
                                <span className="font-medium text-foreground">{t('Pay gratuity anyway (Art. 80 override)')}</span>
                                <span className="block text-xs text-muted-foreground mt-0.5">
                                  {t('Waive forfeiture and pay the full calculated gratuity despite the Art. 80 ground.')}
                                </span>
                              </span>
                            </label>

                            {preview && (
                              <p className="text-xs font-medium text-warning ps-7">
                                {art80ProcedureConfirmed && art80WarningOnFile && !allowGratuityDespiteCause
                                  ? t('Forfeiture active: gratuity is forfeited (0) under Art. 80.', { nsSeparator: false })
                                  : t('Gratuity paid: {{amount}} (forfeiture not applied).', { amount: `${formatNumber(preview.finalGratuity)} ${currencyLabel}`, nsSeparator: false })}
                              </p>
                            )}
                          </div>
                        )}

                        <div className="pt-2">
                          <button
                            onClick={handlePreview}
                            disabled={previewing}
                            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            {previewing && (
                              <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                              </svg>
                            )}
                            {t('Preview EOS Calculation')}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Preview result */}
                    {preview && (
                      <div className="flex flex-col gap-4">
                        <EosBreakdownTable
                          steps={preview.steps}
                          finalGratuity={preview.finalGratuity}
                          currencyLabel={currencyLabel}
                        />
                        <EosComplianceChecklist checks={preview.complianceFlags} />
                      </div>
                    )}

                    {/* What-if comparison (HR/Admin planning tool — no side effects) */}
                    {isHr && (
                      <ScenarioComparison
                        base={{
                          userId,
                          endOfServiceId,
                          departureReason,
                          contractType,
                          departureDate,
                          allowGratuityDespiteCause,
                          art80ProcedureConfirmed,
                          art80WarningOnFile,
                        }}
                        currencyLabel={currencyLabel}
                        reasonOptions={ALL_DEPARTURE_REASONS}
                      />
                    )}

                    {/* Confirm action bar */}
                    {isHr && (
                      <StickyActionBar
                        hasUnsavedChanges={!!preview}
                        isSaving={confirming}
                        onReset={handleReset}
                        onSave={handleConfirm}
                        t={t}
                        saveLabel={t('Confirm & Set EOS')}
                        resetLabel={t('Clear Preview')}
                      />
                    )}

                    {/* Audit log (HR/Admin only — hidden from employee self-view) */}
                    {isHr && (
                    <div className="bg-card border border-border rounded-xl p-5 flex flex-col gap-3">
                      <h4 className="text-sm font-semibold text-foreground">{t('EOS Audit Trail')}</h4>
                      {auditLoading ? (
                        <p className="text-sm text-muted-foreground">{t('Loading...')}</p>
                      ) : auditLog.length === 0 ? (
                        <p className="text-sm text-muted-foreground">{t('No audit entries yet')}</p>
                      ) : (
                        <ul className="flex flex-col gap-2">
                          {auditLog.map(entry => (
                            <li key={entry.id} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 py-2 border-t border-border text-sm">
                              <span className="text-muted-foreground shrink-0">
                                {appDate(parseApiUtc(entry.calculatedOn))}
                              </span>
                              <span className="text-foreground font-medium">{entry.eosTypeName}</span>
                              <span className="text-muted-foreground">{entry.serviceYearsDecimal.toFixed(2)} {t('yrs')}</span>
                              <span className="font-semibold text-primary ms-auto">
                                {formatNumber(entry.finalAmount)} {currencyLabel}
                              </span>
                              {entry.capApplied && (
                                <span className="text-xs text-amber-600 font-medium">{t('Cap applied')}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    )}
                  </>
                )}
              </>
            )}

            {/* Overlay spinner for async operations */}
            {isLoading && context && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                <svg className="animate-spin h-8 w-8 text-primary" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              </div>
            )}

            <ToastRenderer toast={toast} onDismiss={dismissToast} />
          </div>
        </UserPayroll>
      </UserProfile>
    </div>
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const mapStateToProps = (state: any) => ({
  role: state.user.role,
  hireDate: state.user.selectedUser?.hireDate ?? state.user.hireDate,
  loggedInUserId: state.user.userId,
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default connect(mapStateToProps)(EOService) as any;
